import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  Home, Pill, BookOpen, FileText, ClipboardCheck, Activity,
  Microscope, Shield, LogOut, User, Zap, Users,
} from "lucide-react";

const NAV_ITEMS = [
  { path: "/app",           label: "Home",               icon: Home },
  { path: "/app/vitamin",   label: "Vitamin Recommender", icon: Pill },
  { path: "/app/literature",label: "Literature Q&A",      icon: BookOpen },
  { path: "/app/report",    label: "Reports",             icon: FileText },
  { path: "/app/regulatory",label: "Regulatory Drafts",   icon: ClipboardCheck },
  { path: "/app/interpret", label: "Data Interpretation", icon: Activity },
  { path: "/app/analyse",   label: "Report Analysis",     icon: Microscope },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { username, email, isAdmin, logout } = useAuth();

  const isActive = (path: string) =>
    path === "/app" ? location.pathname === "/app" : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen flex flex-col bg-ay-bg text-white">
      {/* Header */}
      <header className="border-b border-ay-border bg-ay-surface/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="relative w-7 h-7">
              <div className="absolute inset-0 rounded-lg bg-ay-cyan/20" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Zap size={14} className="text-ay-cyan" />
              </div>
            </div>
            <span className="font-bold text-base">
              <span className="text-white">Alpha</span>
              <span className="text-ay-cyan">You</span>
              <span className="text-ay-muted font-normal text-xs ml-1">AI</span>
            </span>
          </Link>

          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-2 text-ay-muted">
              <User size={14} />
              <span className="hidden sm:inline text-xs">{email || username}</span>
            </div>
            {isAdmin && (
              <span className="badge-emerald text-[10px] px-2 py-0.5">ADMIN</span>
            )}
            <button
              onClick={logout}
              className="flex items-center gap-1.5 text-ay-muted hover:text-white transition-colors text-xs"
              aria-label="Sign out"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 max-w-7xl mx-auto w-full">
        {/* Sidebar */}
        <nav className="w-56 border-r border-ay-border py-4 hidden md:block flex-shrink-0">
          <ul className="space-y-0.5 px-2">
            {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
              <li key={path}>
                <Link
                  to={path}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    isActive(path)
                      ? "bg-ay-cyan/10 text-ay-cyan border border-ay-cyan/20 font-semibold"
                      : "text-ay-muted hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon size={16} /> {label}
                </Link>
              </li>
            ))}
            {isAdmin && (
              <>
                <li>
                  <Link
                    to="/app/admin"
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                      location.pathname === "/app/admin"
                        ? "bg-ay-emerald/10 text-ay-emerald border border-ay-emerald/20 font-semibold"
                        : "text-ay-muted hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Shield size={16} /> Admin Console
                  </Link>
                </li>
                <li>
                  <Link
                    to="/app/crm"
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                      location.pathname === "/app/crm"
                        ? "bg-ay-cyan/10 text-ay-cyan border border-ay-cyan/20 font-semibold"
                        : "text-ay-muted hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Users size={16} /> CRM Dashboard
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>

        {/* Main content */}
        <main className="flex-1 p-6 min-w-0">{children}</main>
      </div>

      <footer className="border-t border-ay-border py-3 text-center text-xs text-ay-muted">
        AlphaYou v0.1 · Not medical advice · eu-west-2 (London) · GDPR compliant
      </footer>
    </div>
  );
}
